import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useGetScheduledPosts } from '@onesource/services';
import { useState, type FC } from 'react';
import { RiTimeLine } from 'react-icons/ri';

import { PostCard } from './post-card';

interface ScheduledPostsSectionProps {
  /** Feed sub-space the scheduled posts belong to. */
  subSpaceId: string;
  /** Parent space id — forwarded to PostCard (matches the feed list). */
  parentSpaceId: string;
  /** Only admins can schedule; skip the request otherwise. */
  enabled: boolean;
}

/**
 * Admin-only management surface for the viewer's own posts scheduled to publish
 * later (hidden from the public feed). Collapsed by default; each item reuses
 * PostCard so rescheduling/editing happens through the same edit form.
 */
export const ScheduledPostsSection: FC<ScheduledPostsSectionProps> = ({ subSpaceId, parentSpaceId, enabled }) => {
  const [open, setOpen] = useState(false);
  const { data } = useGetScheduledPosts(subSpaceId, enabled);
  const posts = data?.posts ?? [];

  // Nothing to manage → render nothing (keeps the feed uncluttered, and avoids a
  // skeleton that would flash then vanish for the common empty case).
  if (!enabled || posts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        border: '1px solid #E6D9C2',
        borderRadius: '12px',
        bgcolor: '#FBF7EF',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <RiTimeLine size={18} color="#B7791F" />
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontSize: 14, fontWeight: 600, color: '#8A6D3B' }}>
          Scheduled ({posts.length})
        </Typography>
        <Box sx={{ flex: 1 }} />
        {open ? <ExpandLessIcon sx={{ color: '#8A6D3B' }} /> : <ExpandMoreIcon sx={{ color: '#8A6D3B' }} />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={2} sx={{ px: 2, pb: 2 }}>
          {posts.map(post => (
            <PostCard key={post.id} post={post} spaceId={parentSpaceId} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
